/**
 * Widget Component Registry
 * Maps widget component names (from mechanic definitions) to React components.
 * Extend this file when adding new widget components.
 */
import React from "react";
import type { ResolvedWidget } from "@/lib/mechanics/registry";
import type { WidgetAction } from "@/lib/mechanics/types";

// Widget components
import { CharacterSheet } from "./CharacterSheet";
import { InventoryPanel } from "./InventoryPanel";
import { QuestJournal } from "./QuestJournal";
import { CombatPanel } from "./CombatPanel";
import { ConditionsWidget } from "./ConditionsWidget";
import { CardHand } from "./CardHand";
import { SurvivalWidget } from "./SurvivalWidget";
import { MagicWidget } from "./MagicWidget";
import { ReputationWidget } from "./ReputationWidget";
import { TimelineWidget, TimelineStatus } from "./TimelineWidget";
import { GoldWidget } from "./GoldWidget";
import { XPBarWidget, XPStatus } from "./XPBarWidget";
import { RegionMapWidget, RegionStatusWidget } from "./RegionWidget";
import { TimeStatus } from "./TimeStatusWidget";
import { MoralityWidget, MoralityStatus } from "./MoralityWidget";
import { LoreCodexPanel } from "./LoreCodexPanel";
import { EncounterLogWidget } from "./EncounterLogWidget";
import { DecisionJournal } from "./DecisionJournal";
import { ResourceBarWidget } from "./ResourceBarWidget";
import { ResourcePanel } from "./ResourcePanel";
import { OrgStatusWidget } from "./OrgStatusWidget";
import { OrgOverviewPanel } from "./OrgOverviewPanel";
import { TerritoryStatusWidget, TerritoryPanel } from "./TerritoryPanel";
import { WorldMapWidget, LocationStatusWidget } from "./WorldMapWidget";
import { InitiativeOrderWidget } from "./InitiativeOrderWidget";
import { RestWidget, RestStatus } from "./RestWidget";
import { ResearchPanel, ResearchStatusWidget } from "./ResearchPanel";
import { POVPanel } from "./POVPanel";
import { POVStatusWidget } from "./POVStatusWidget";
import { OperationsPanel } from "./OperationsPanel";
import { OperationsStatusWidget } from "./OperationsStatusWidget";

// Registry mapping component name → React component
const WIDGET_COMPONENTS: Record<string, React.ComponentType<any>> = {
  CharacterSheet,
  InventoryPanel,
  QuestJournal,
  CombatPanel,
  ConditionsWidget,
  CardHand,
  SurvivalWidget,
  MagicWidget,
  ReputationWidget,
  TimelineWidget,
  TimelineStatus,
  GoldWidget,
  XPBarWidget,
  XPStatus,
  ProgressionWidget: XPBarWidget,
  RegionMapWidget,
  RegionStatusWidget,
  TimeStatus,
  MoralityWidget,
  MoralityStatus,
  LoreCodexPanel,
  EncounterLogWidget,
  DecisionJournal,
  ResourceBarWidget,
  ResourcePanel,
  OrgStatusWidget,
  OrgOverviewPanel,
  TerritoryStatusWidget,
  TerritoryPanel,
  WorldMapWidget,
  LocationStatusWidget,
  InitiativeOrderWidget,
  RestWidget,
  RestStatus,
  ResearchPanel,
  ResearchStatusWidget,
  POVStatusWidget,
  POVPanel,
  // Unified Operations (new)
  OperationsPanel,
  OperationsStatusWidget,
};

export type WidgetActionHandler = (action: WidgetAction, params: Record<string, string>) => void;

/**
 * Render a resolved widget using the component registry.
 * Returns null if the component isn't registered.
 * 
 * @param onAction - callback for widget-dispatched actions (e.g. "Use Item")
 */
export function renderWidget(
  resolved: ResolvedWidget,
  extraProps?: Record<string, any>,
  onAction?: WidgetActionHandler,
): React.ReactElement | null {
  const Component = WIDGET_COMPONENTS[resolved.widget.component];
  if (!Component) return null;

  return React.createElement(Component, {
    key: resolved.widget.id,
    ...resolved.data,
    ...extraProps,
    // Pass widget actions and handler if defined
    widgetActions: resolved.widget.actions,
    onWidgetAction: onAction,
  });
}

export function getWidgetComponent(componentName: string): React.ComponentType<any> | undefined {
  return WIDGET_COMPONENTS[componentName];
}

export { WIDGET_COMPONENTS };
